import { FieldValue, Timestamp, serverTimestamp } from 'firebase/firestore';
import { getAuthHeaders } from './apiUtils';
import { auth } from './firebase';

// Share concurrent list reads only. No completed notes or private detail
// responses are cached between visits. Firebase User identity scopes a session,
// so signing back into the same UID cannot reuse the previous session's reads.
let sessionUser;
let patientLoads = new Map();
function requireSession(user) {
  if (!user?.uid || auth.currentUser !== user) {
    throw Object.assign(new Error('Your session changed. Please reload this patient.'), { code: 'session-changed' });
  }
}
function patientScope(user, patientId) {
  if (sessionUser !== user) {
    sessionUser = user;
    patientLoads = new Map();
  }
  if (!patientLoads.has(patientId)) patientLoads.set(patientId, { generation: 0, pending: null, writes: new Set() });
  return patientLoads.get(patientId);
}

async function sendRequest(user, url, method, body) {
  requireSession(user);
  const headers = await getAuthHeaders();
  requireSession(user);
  const response = await fetch(url, { method, headers, ...(body ? { body: JSON.stringify(encode(body)) } : {}) });
  requireSession(user);
  if (response.status === 404 && method === 'GET') return null;
  const result = await response.json();
  requireSession(user);
  if (!response.ok) throw Object.assign(new Error(result.error || 'Document request failed'), { status: response.status });
  return result;
}

async function readList(user, scope, url) {
  requireSession(user);
  if (scope.writes.size) {
    await Promise.allSettled([...scope.writes]);
    return readList(user, scope, url);
  }
  if (!scope.pending) {
    const generation = scope.generation;
    const entry = {};
    scope.pending = entry;
    entry.promise = sendRequest(user, url, 'GET').then(result => {
      // A write overlapped this read. Wait for it and join the fresh read rather
      // than publishing a pre-edit snapshot after save/archive/creation.
      return scope.generation === generation ? result : readList(user, scope, url);
    }).finally(() => {
      if (scope.pending === entry) scope.pending = null;
    });
  }
  return scope.pending.promise;
}

export const patientDocumentPath = path => /^\/?patients\/([^/]+)\/documents(?:\/([^/]+))?$/.exec(path);
const encode = value => {
  if (value instanceof FieldValue) {
    if (value.isEqual(serverTimestamp())) return { __fgServerTimestamp: true };
    throw new Error('Unsupported document field transform');
  }
  if (value instanceof Date) return { __fgTimestampMs: value.getTime() };
  if (typeof value?.toMillis === 'function') return { __fgTimestampMs: value.toMillis() };
  if (Array.isArray(value)) return value.map(encode);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, encode(child)]));
  return value;
};
const revive = value => {
  if (Array.isArray(value)) return value.map(revive);
  if (value && typeof value === 'object') {
    if (typeof value._seconds === 'number' && typeof value._nanoseconds === 'number') return new Timestamp(value._seconds, value._nanoseconds);
    return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, revive(child)]));
  }
  return value;
};
export async function patientDocumentRequest(path, method = 'GET', body) {
  const match = patientDocumentPath(path);
  if (!match) throw new Error('Invalid patient document path');
  const url = `/api/v3/patients/${encodeURIComponent(match[1])}/documents${match[2] ? `/${encodeURIComponent(match[2])}` : ''}`;
  const user = auth.currentUser;
  requireSession(user);
  const scope = patientScope(user, match[1]);
  if (method === 'GET') {
    // Revive separately for each consumer: one caller sorting/editing its data
    // must not mutate another caller's copy of the shared response.
    return revive(await (!match[2] ? readList(user, scope, url) : sendRequest(user, url, method)));
  }
  scope.generation += 1;
  scope.pending = null;
  const writing = sendRequest(user, url, method, body);
  scope.writes.add(writing);
  try {
    return revive(await writing);
  } finally {
    scope.writes.delete(writing);
    scope.generation += 1;
    scope.pending = null;
  }
}
